import get from 'lodash/get';
import extend from 'lodash/extend';
import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';

import deSlugify from './string_util';
import adsConfiguration from '../config/ads';

export function figureOutPriorityTag(originalTags) {
  const tags = cloneDeep(originalTags);
  const tagMatch = {
    slug: '',
    name: ''
  };

  if (get(tags, 'length')) {
    // We only care about first tag that is elibigle
    tags.reverse();
    tags.forEach(tag => {
      const tagSlug = tag.slug;
      const tagName = tag.name;
      // TODO: clean this up
      const matchedPriorityTag = get(
        get(adsConfiguration, 'priorityTags')[tagSlug],
        'slug'
      );
      if (tagMatch.slug === '' && matchedPriorityTag) {
        tagMatch.slug = matchedPriorityTag;
        tagMatch.name = matchedPriorityTag.replace(/-/gi, ' ');
      }

      if (tagName.indexOf('*') !== -1) {
        tagMatch.slug = tagName;
        tagMatch.name = tagName;
      }
    });
  }
  return tagMatch;
}

// construct adzone from a list of tags by looking up adtag mapping
export function figureOutAdzone(
  originalTags = [],
  category = 'news',
  adsConfig,
  pageType
) {
  const typeMap = {
    homepage() {
      return 'homepage/homepage';
    },
    categoryPage() {
      return `${category}/${category}`;
    },
    postPage() {
      let adzone = 'misc/misc';
      const tagList = cloneDeep(originalTags);

      if (get(tagList, 'length')) {
        // We only care about first tag that is elibigle
        tagList.reverse();

        // Matches tags against our existing list, if matched, set that tag as priority
        let tagMatch = null;

        // If there's a star tag, that tag will override the category in ad call and analytics
        let starTagMatch = null;

        tagList.forEach(tag => {
          const tagSlug = tag.slug;
          const tagName = tag.name;
          const matchedPriorityTag = get(
            get(adsConfiguration, 'priorityTags')[tagSlug],
            'slug'
          );
          if (tagMatch === null && matchedPriorityTag) {
            tagMatch = matchedPriorityTag;
          }

          if (tagName.indexOf('*') !== -1) {
            starTagMatch = tagName.replace(/\s+/g, '_');
          }
        });

        // adzone logic:
        // if the tag is found in the tag mapping, then use that mapping as tag
        // topic is category of the post
        // otherwise, defaults to misc

        if (starTagMatch) {
          return `advertorial/${starTagMatch}`;
        } else if (tagMatch) {
          const tagMatchDeslugged = deSlugify(tagMatch);
          adzone = isString(category)
            ? `${category}/${tagMatchDeslugged}`
            : `misc/${tagMatchDeslugged}`;
          if (starTagMatch) {
            adzone = `${starTagMatch}/${tagMatchDeslugged}`;
          }
        } else {
          adzone = isString(category) ? `${category}/misc` : 'misc/misc';
        }
      } else {
        adzone = isString(category) ? `${category}/misc` : 'misc/misc';
      }
      return adzone;
    },
    tagPage() {
      const tagLanderOverride = get(
        get(adsConfiguration, 'tagLanderOverrides')[category.slug],
        'slug'
      );

      if (category.name) {
        if (category.name.indexOf('*') !== -1) {
          return `advertorial/${category.name.replace(/\s+/g, '_')}`;
        }
        if (isString(tagLanderOverride)) {
          return `advertorial/${tagLanderOverride}`;
        }
        const matchedTopicCategory =
          get(
            get(adsConfiguration, 'priorityTags')[category.slug],
            'category'
          ) || 'misc';
        return `${matchedTopicCategory}/${deSlugify(category.slug)}`;
      }
      return '';
    },
    searchPage() {
      return 'search/search';
    },
    authorPage() {
      return 'author/author';
    },
    wpPage() {
      return 'page/page';
    },
    videoPage() {
      const video = get(category, 'data.playlist[0]', {});
      const series = video.series || 'video';
      return `video/${series}`;
    },
    videoPlaylistPage() {
      const playlist = get(category, 'slug', '');
      return `video/${playlist}`;
    },
    videoIndexPage() {
      return `video/video`;
    },
    productPage() {
      return 'product/product';
    }
  };

  if (typeMap[pageType]) {
    return typeMap[pageType]();
  }

  // Default adzone
  return 'misc/misc';
}

/*
  Builds the following model params for ad key val generation
  name: topic (should only show on hp, topic, tag pages)
  type: homepage, post, postList, user
  kind: homepage, article, slideshow, video, collection profile
  url: url of the page (full url with http/https)
  topic: topic of the post
  supertag: primary tag
  tags: rest of the tag
  author: author name
*/
export function buildPostAdModel(adsConfig, site, pageData, pageType) {
  const tagList = get(pageData, 'tags') || [];

  let category = get(pageData, 'categories[0].slug');

  // On topic pages category string is being passed in as the pageData param
  if (!category) {
    category = pageData;
  }
  let contentType = 'article';
  if (get(pageData, 'posterSlideshow.length')) {
    contentType = 'slideshow';
  } else if (
    get(pageData, 'hasVideoPoster') === true ||
    get(pageData, 'posterVideo.feedId')
  ) {
    contentType = 'video';
  }

  const typeMap = {
    homepage() {
      return {
        name: 'homepage',
        type: 'homepage',
        kind: 'homepage',
        url: `https://www.${site}.com`
      };
    },
    categoryPage() {
      if (!category) {
        category = 'news';
      }

      return {
        type: 'category',
        kind: 'category',
        name: category,
        url: `https://www.${site}.com/${category}`
      };
    },
    tagPage() {
      let name = '';
      const tags = [];
      const slug = get(pageData, 'slug');
      const pageName = get(pageData, 'name');
      const categoryMatch = get(
        get(adsConfiguration, 'priorityTags')[slug],
        'category'
      );

      if (pageName) {
        if (pageName.indexOf('*') !== -1) {
          name = 'advertorial';
          tags.push(name);
          tags.push('custom');
          tags.push(slug);
        } else {
          if (categoryMatch) {
            name = categoryMatch;
            tags.push(categoryMatch);
            tags.push('custom');
          }
          tags.push(slug);
        }
      } else {
        tags.push(slug);
      }

      return {
        type: 'tag',
        kind: 'tag',
        name,
        tags,
        url: `https://www.${site}.com/section/${slug}`
      };
    },
    searchPage() {
      return {
        kind: 'search',
        type: 'search',
        name: 'search'
      };
    },
    authorPage() {
      const author = get(pageData, 'slug');
      return {
        kind: 'author',
        type: 'author',
        name: 'author',
        author
      };
    },
    postPage() {
      const tags = [];
      tagList.forEach(topicItem => {
        tags.push(topicItem.slug);
      });

      return {
        id: get(pageData, 'id'),
        type: 'post',
        kind: contentType,
        url: `https://www.fastcompany.com/${get(pageData, 'id')}/${get(
          pageData,
          'slug'
        )}`,
        topic: category,
        supertag: '',
        tags,
        author: get(pageData, 'author.slug')
      };
    },
    wpPage() {
      return {
        kind: 'page',
        type: 'page',
        name: 'page'
      };
    },
    videoPage() {
      return {
        kind: 'video',
        type: 'video',
        name: 'video'
      };
    },
    videoIndexPage() {
      return {
        kind: 'video',
        type: 'video',
        name: 'video'
      };
    },
    videoPlaylistPage() {
      return {
        kind: 'video',
        type: 'video',
        name: 'video'
      };
    },
    productPage() {
      return {
        kind: 'product',
        type: 'product',
        name: 'product'
      };
    }
  };

  const fn = typeMap[pageType] || function noop() {};

  const model = extend(
    {},
    {
      site,
      adzone: figureOutAdzone(tagList, category, adsConfig, pageType)
    },
    fn()
  );

  return {
    config: adsConfig,
    model
  };
}

export default buildPostAdModel;
